import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AvatarContainer,
  StyledAvatar,
} from '@/shared/components/ui/avatar/avatar.styled';
import {
  TAvatarProps,
  TUserInfo,
} from '@/shared/components/ui/avatar/avatar.types';
import { Chip } from '@/shared/components/ui/chip/chip';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import { UserSourceChip } from '@/shared/features/rbac/components/user-source-chip/user-source-chip';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useLoggedInUser } from '@/shared/hooks/use-logged-in-user/use-logged-in-user';
import { useReadUserQuery } from '@/shared/redux/api/ident/ident-api';
import { UserSourceEnum } from '@/shared/types/types.generated';
import { getFullname } from '@/shared/utils/format';

export const Avatar: React.FC<TAvatarProps> = ({
  user,
  userId,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const isNotAvailable = userId === 'not_available';
  const { rbacPermissionChecks } = useRbacConfig();
  const { isLoggedInUser, isOwner } = useLoggedInUser();
  const hasPermission = usePermissions(rbacPermissionChecks.readUser?.(userId));
  const { data, isLoading } = useReadUserQuery(
    { userId: userId! },
    { skip: !!user || !userId || isNotAvailable || !hasPermission },
  );
  const userData = data?.readUser as TUserInfo;

  const validUser = user ?? userData;
  const _userId = validUser?.userId ?? userId;

  // Display hyphen for not available or deleted users
  if (isNotAvailable || (!validUser && !isLoading)) {
    return <Typography variant="p8">-</Typography>;
  }

  if (isLoading) {
    return <Typography variant="p7">{t('common:loading')}</Typography>;
  }

  const renderUsername = (): string => {
    return getFullname(validUser);
  };

  const getAvatarContent = () => {
    return (
      <Typography variant="p9" color="textPrimary">
        {renderUsername().at(0)}
      </Typography>
    );
  };

  const renderAvatar = () => {
    if (variant === 'nameOnly' || variant === 'nameOnlyLight') {
      return null;
    }
    return (
      <StyledAvatar $color={validUser?.isSystemUser ? 'grey' : undefined}>
        {getAvatarContent()}
      </StyledAvatar>
    );
  };

  const renderName = () => {
    if (variant === 'avatarOnly') {
      return null;
    }
    const typographyVariant = variant === 'nameOnlyLight' ? 'p10' : 'p7';
    const typographyColor =
      variant === 'nameOnlyLight' ? 'textSecondary' : 'textPrimary';
    return (
      <Typography variant={typographyVariant} color={typographyColor}>
        {renderUsername()}
      </Typography>
    );
  };

  const renderTooltip = () => {
    if (validUser?.isSystemUser) {
      return null;
    }

    const showSource =
      validUser?.source && validUser?.source !== UserSourceEnum.Sda;
    let baseTooltip = validUser?.email;

    if (variant === 'avatarOnly') {
      const email = validUser?.email ? ` (${validUser.email})` : '';
      baseTooltip = `${renderUsername()}${email}`;
    }

    return (
      <Stack component="span" direction="row" gap="0.5rem" alignItems="center">
        {baseTooltip}
        {showSource && <UserSourceChip userSource={validUser.source!} />}
        {isOwner(_userId) && <Chip status="blue" label={t('common:owner')} />}
        {isLoggedInUser(_userId) && (
          <Chip status="green" label={t('rbac:logged_user_chip')} />
        )}
      </Stack>
    );
  };

  return (
    <Tooltip title={renderTooltip()} wrapper="default" noMaxWidth>
      <AvatarContainer>
        {renderAvatar()}
        {renderName()}
      </AvatarContainer>
    </Tooltip>
  );
};
